import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

import useMediaQuery from '@mui/material/useMediaQuery';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import OutlinedInput from '@mui/material/OutlinedInput';
import Button from '@mui/material/Button';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';

// project imports
import Drawer from './Drawer';
import Header from './Header';
import Footer from './Footer';
import Loader from 'components/Loader';
import Breadcrumbs from 'components/@extended/Breadcrumbs';

import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { chamaApi } from 'api/chama';

// ==============================|| MAIN LAYOUT ||============================== //

export default function DashboardLayout() {
  const { menuMasterLoading } = useGetMenuMaster();
  const downXL = useMediaQuery((theme) => theme.breakpoints.down('xl'));

  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: 'Hi. Ask anything about your chama operations.' }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [chatError, setChatError] = useState('');

  useEffect(() => {
    handlerDrawerOpen(!downXL);
  }, [downXL]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setSending(true);
    setChatError('');

    try {
      const data = await chamaApi.aiChat(text, next.slice(-10));
      setMessages((prev) => [...prev, { role: 'assistant', content: data.reply }]);
    } catch (e) {
      setChatError(e.message || 'Failed to reach chatbot.');
    } finally {
      setSending(false);
    }
  };

  if (menuMasterLoading) return <Loader />;

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <Header />
      <Drawer />

      <Box component="main" sx={{ width: 'calc(100% - 260px)', flexGrow: 1, p: { xs: 2, sm: 3 } }}>
        <Toolbar sx={{ mt: 'inherit' }} />
        <Box
          sx={{
            ...{ px: { xs: 0, sm: 2 } },
            position: 'relative',
            minHeight: 'calc(100vh - 110px)',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Breadcrumbs />
          <Outlet />
          <Footer />
        </Box>
      </Box>

      {chatOpen ? (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            right: 24,
            bottom: 90,
            width: { xs: 'calc(100vw - 32px)', sm: 380 },
            maxHeight: '70vh',
            zIndex: 1400,
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, bgcolor: 'primary.main', color: '#fff' }}>
            <Typography variant="subtitle1">Chama Chat</Typography>
            <IconButton size="small" onClick={() => setChatOpen(false)} sx={{ color: '#fff' }}>
              <CloseOutlined />
            </IconButton>
          </Stack>

          <Stack sx={{ p: 1.25, gap: 1, maxHeight: '45vh', overflow: 'auto', bgcolor: 'background.paper' }}>
            {messages.map((m, i) => (
              <Stack key={`${m.role}-${i}`} sx={{ alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <Box sx={{ px: 1.2, py: 0.8, borderRadius: 1.5, maxWidth: '86%', bgcolor: m.role === 'user' ? 'primary.lighter' : 'grey.100' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.content}</Typography>
                </Box>
              </Stack>
            ))}
          </Stack>

          <Stack sx={{ p: 1.25, gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <OutlinedInput
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type message..."
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
            />
            <Button variant="contained" onClick={sendMessage} disabled={sending || !input.trim()}>
              {sending ? 'Sending...' : 'Send'}
            </Button>
            {chatError ? <Typography color="error.main" variant="caption">{chatError}</Typography> : null}
          </Stack>
        </Paper>
      ) : null}

      <Tooltip title="Open Chat">
        <Fab
          color="primary"
          aria-label="chatbot"
          onClick={() => setChatOpen((prev) => !prev)}
          sx={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            zIndex: 1400
          }}
        >
          <MessageOutlined />
        </Fab>
      </Tooltip>
    </Box>
  );
}
